import UtilI18N from "@dia-ui/util_i18n";
import React, { useContext } from "react";
import { AppContext } from "../../context/AppContext";
import { PartnerFile } from "../../services/PartnerFileService";
import { formatBytes, formatTimestamp } from "../../utils/format";

/**
 * one supplier with its resolved identity and the files it provides to the
 * logged in partner
 */
export type FileExchangeGroup = {
  supplierId: string;
  name: string;
  hashId: string;
  files: PartnerFile[];
};

/**
 * used and allowed storage of the logged in partner as a supplier, in bytes
 */
export type FileQuota = {
  used: number;
  max: number;
};

/**
 * the files of a single folder. an empty path is the root of the partner's own
 * directory, which has no folder name to show
 */
export type FileFolder = {
  path: string;
  files: PartnerFile[];
};

/**
 * getLocale
 * @param I18N
 */
export function getLocale(I18N: UtilI18N): string {
  return I18N.getLocale?.() || "de";
}

/**
 * formatPhrase
 * fills the %s placeholders of a translated phrase in order. UtilI18N.t() accepts
 * arguments but crashes on them in this build ("dist.apply is not a function" out
 * of its sprintf import), so the substitution happens here instead
 * @param template
 * @param values
 */
export function formatPhrase(template: string, values: (string | number)[]): string {
  let index = 0;

  return template.replace(/%s/g, () => {
    const value = values[index];
    index += 1;

    return value === undefined ? "" : String(value);
  });
}

/**
 * getFolderPath
 * the folder a file sits in, taken from its frontend path. a file in the root of
 * the directory has none, and then nothing is shown
 * @param file
 */
export function getFolderPath(file: PartnerFile): string {
  const path = (file?.frontendPath || "").replace(/\\/g, "/"),
    index = path.lastIndexOf("/");

  return index > 0 ? path.slice(0, index) : "";
}

/**
 * groupByFolder
 * sorts a flat file list into its folders. the root comes first, because that is
 * where a supplier drops the files it does not sort at all, the folders after it
 * in alphabetical order
 * @param files
 * @param locale
 */
export function groupByFolder(files: PartnerFile[], locale: string): FileFolder[] {
  const folders: FileFolder[] = [],
    byPath: { [path: string]: FileFolder } = {};

  (files || []).forEach((file) => {
    const path = getFolderPath(file);

    if (!byPath[path]) {
      byPath[path] = { path, files: [] };
      folders.push(byPath[path]);
    }

    byPath[path].files.push(file);
  });

  folders.forEach((folder) => {
    // numbered files in human order: "Preisliste 2" before "Preisliste 10"
    folder.files.sort((a, b) => (a?.filename || "").localeCompare(
      b?.filename || "",
      locale,
      { numeric: true, sensitivity: "base" }
    ));
  });

  folders.sort((a, b) => {
    if (!a.path || !b.path) {
      // the root against a folder: the root wins, two roots cannot happen
      return a.path ? 1 : (b.path ? -1 : 0);
    }

    return a.path.localeCompare(b.path, locale, {
      numeric: true,
      sensitivity: "base"
    });
  });

  return folders;
}

/**
 * FileTableProps
 * supplier: the files of one supplier, which carry a visibility of their own and
 * whose folder is shown under the file name
 * provided: the partner's own files, already grouped by visibility and folder by
 * the view around it, so both columns would only repeat their heading
 */
type FileTableProps = {
  files: PartnerFile[];
  label: string;
  variant: "supplier" | "provided";
  pendingFileId: string;
  onDownload: (file: PartnerFile) => void;
};

/**
 * FileTable
 * the file rows of both views. the design system draws every bare table as a
 * flex mock table, which the [data-file-exchange] block in theme.scss undoes --
 * so both views have to keep to this structure and to these attributes
 * @param props
 * @constructor
 */
export const FileTable: React.FC<FileTableProps> = ({
  files,
  label,
  variant,
  pendingFileId,
  onDownload
}) => {
  const { I18N } = useContext(AppContext);

  const locale: string = getLocale(I18N),
    isSupplier: boolean = variant === "supplier";

  return (
    // the container scrolls sideways on narrow viewports, so it needs to be
    // reachable and scrollable by keyboard as well
    <div
      data-file-exchange-table-scroll
      tabIndex={0}
      role="region"
      aria-label={label}
    >
      { /** the variant is always written out: react drops an attribute set to
            undefined, and the whole table reset in theme.scss hangs off
            [data-file-exchange-table] — without it the design system draws the
            rows as flex again and every row sizes its columns on its own **/ }
      <table data-file-exchange-table={variant}>
        <caption data-file-exchange-table-caption>{label}</caption>
        <thead>
          <tr>
            <th scope="col" data-file-exchange-column-file>{I18N.t("Datei")}</th>
            {isSupplier && (
              <th scope="col" data-file-exchange-column-visibility>{I18N.t("Sichtbarkeit")}</th>
            )}
            <th scope="col" data-file-exchange-column-size>{I18N.t("Größe")}</th>
            <th scope="col" data-file-exchange-column-modified>{I18N.t("Geändert")}</th>
            <th scope="col" data-file-exchange-column-action>{I18N.t("Aktion")}</th>
          </tr>
        </thead>
        <tbody>
          {files.map((file) => {
            const folder = isSupplier ? getFolderPath(file) : "",
              isPending = !!file.fileId && pendingFileId === file.fileId;

            return (
              <tr key={file.fileId || file.frontendPath || file.path}>
                <td data-file-exchange-cell-file>
                  <span data-file-exchange-filename>{file.filename}</span>
                  {!!folder && (
                    <small data-file-exchange-folder>{folder}</small>
                  )}
                </td>
                {isSupplier && (
                  <td
                    data-file-exchange-cell-visibility
                    data-private={file.private ? "" : undefined}
                  >
                    {file.private
                      ? I18N.t("Nur für mich")
                      : I18N.t("Für alle Kunden")}
                  </td>
                )}
                <td data-file-exchange-cell-size>
                  {formatBytes(file.size, locale)}
                </td>
                <td data-file-exchange-cell-modified>
                  {formatTimestamp(file.lastModified, locale)}
                </td>
                <td data-file-exchange-cell-action>
                  <button
                    type="button"
                    data-file-exchange-download
                    data-small
                    data-primary-dark
                    disabled={isPending}
                    aria-label={formatPhrase(
                      I18N.t("%s herunterladen"),
                      [file.filename || ""]
                    )}
                    onClick={() => onDownload(file)}
                  >
                    {isPending ? I18N.t("Wird vorbereitet …") : (
                      <>
                        <i data-icon-before="download" aria-hidden="true"></i>
                        {I18N.t("Herunterladen")}
                      </>
                    )}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
